import {
  BehaviorSubject,
  map,
  skipWhile,
  Subject,
  Subscription,
  take,
  tap,
} from 'rxjs';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { IHeaderModel } from '@types-custom/models/ui/header.model';
import { MenuService } from '@official/services/menu/menu.service';
import { NavigationEnd, Router, Event } from '@angular/router';
import { RouteNamesEnum } from '@official/routes/routes';
import { generateIndicatorsModel } from '@official/models/constants/indicators';
import { LoginAuthService } from '@shared/services/login-auth/login-auth.service';

import { ModalService } from '@ui-core/services/modal/modal.service';

import { IndicatorService } from '@shared/services/indicator-service/indicator.service';
import {
  IndicatorModel,
  IndicatorProps,
} from '@types-custom/models/ui/indicators.model';
import { IMenuModel } from '@types-custom/models/ui/menu.model';
import { LocalStorageManagementService } from '@shared/services/local-storage-management/local-storage-management.service';
import { filterMenuBasedOnRoles } from '@shared/utils/functions/filter-menu-based-on-roles';
import { ModalActionHandler } from '@shared/services/action-service/handlers/modal-action-handler';
import { MapActionHandler } from '@shared/services/action-service/handlers/map-action-handler';
import { PanelActionHandler } from '@shared/services/action-service/handlers/panel-action-handler';
import { RouterActionHandler } from '@shared/services/action-service/handlers/router-action-handler';
import { ActionService } from '@shared/services/action-service/action.service';
import { I18nService } from '@sdk/services/i18n/i18n.service';
import environment from "@official-env/environment";
import { TOAST_STATES, ToastService } from "@ui-core/services/toast/toast.service";
import { IAlertModel } from "@shared/models/alerts/alerts.model";
import { NotificationService } from '@shared/services/notifications-service/notification.service';
import { NotifyWsService } from '@shared/services/notify-ws-service/notify-ws.service';
import { NotifyService } from '@ui-core/services/notify/notify.service';
import { notifationsAPIMapper } from '@types-custom/models/ui/notifications-panel-model';
import { NotifyEventComponent } from '@ui-core/controls/notify-event/notify-event.component';
import { NotifyEventCameraComponent } from '@ui-core/controls/notify-event/notify-event-camera/notify-event-camera.component';
@Component({
  selector: 'sisctrl-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  providers: [MenuService, ModalService],
})
export class AppComponent implements OnInit, OnDestroy {
  indicators = new BehaviorSubject<IndicatorModel[] | undefined>(undefined);
  menuEvents$ = new BehaviorSubject<any | undefined>(undefined);
  headerModel$ = new BehaviorSubject<IHeaderModel<any> | undefined>(undefined);
  currentRoute$ = new BehaviorSubject<RouteNamesEnum | undefined>(undefined);
  RouteNamesEnum = RouteNamesEnum;
  isVisibleMenu = true;
  getRuta: Subscription;
  unsubscribier$ = new Subject<void>();
  roles: string[];
  indicatorProps: IndicatorProps;
  isSideMenuVisible: boolean | undefined = undefined;
  actionHandlers = [
    { handler: this.panelActionHandler, service: this.menuService },
    { handler: this.modalActionHandler, service: this.modalService },
    { handler: this.mapActionHandler },
    { handler: this.routerActionHandler },
  ];
  clickDispatcher: Subject<any> = new Subject<any>();

  messageSubscription: Subscription;
  clickSubscription: Subscription;

  subscription: Subscription;

  constructor(
    private router: Router,
    private translator: I18nService,
    private menuService: MenuService,
    private loginAuthService: LoginAuthService,
    private modalService: ModalService,
    private indicatorService: IndicatorService,
    private localStorageManagementService: LocalStorageManagementService,
    private actionService: ActionService,
    private modalActionHandler: ModalActionHandler,
    private mapActionHandler: MapActionHandler,
    private panelActionHandler: PanelActionHandler,
    private routerActionHandler: RouterActionHandler,
    private toast: ToastService,
    private readonly notificationService: NotificationService,
    private notifyWsService: NotifyWsService,
    private notifyService: NotifyService,
  ) {
    this.translator.addFileToResourceBundle('map-viewer', 'map-viewer');
  }

  ngOnInit(): void {
    this.getRuta = this.router.events.subscribe((event: Event) => {
      if (event instanceof NavigationEnd) {
        this.menuService
          .getHeaderModel(this.menuEvents$)
          .pipe(
            take(1),
            map((header) => {
              header.menus = filterMenuBasedOnRoles(header.menus, this.roles);
              return header;
            })
          )
          .subscribe((header) => {
            if (header.menus) {
              this.handleMenu(header.menus, event);
            }
            this.handleMenuLoaded(header);
          });
      }
    });

    this.router.events.subscribe(this.handleRouteEvents.bind(this));
    this.menuService.getHeaderModel(this.menuEvents$).subscribe((a) => {
      this.handleMenuLoaded(a);
    });

    this.indicatorProps = {
      indicatorDataSource: this.indicatorService,
      generateIndicatorsModel: generateIndicatorsModel,
    };

    this.localStorageManagementService.userInfo$.subscribe((res) => {
      this.roles = res?.roles;
    });

    this.actionHandlers.forEach(({ handler, service }) => {
      if (service && 'setService' in handler) {
        handler.setService(service);
      }
      this.actionService.registerActionHandler(handler);
    });
    this.actionService.listenToActions();

    // this.notifyWsService.initWebSocketConnect('notifications', '/api/v1/notification/subscribe/user');
    // this.notifyWsService.initWebSocketConnect();

    this.messageSubscription = this.notifyWsService.getMessage().pipe(
      map((el) => {
        if (el.type?.includes('camera-events')) {
          return { ...el.data, type: el.type };
        }
        else {
          return notifationsAPIMapper(el.data)
        }
      })
    ).subscribe(this.notifyShow.bind(this));
  }

  handleMenu(menu: IMenuModel<any>[], event: NavigationEnd) {
    const mapViewerPages = menu.find(
      (element: any) => element.alt === 'Mapa'
    );

    if (mapViewerPages) {
      mapViewerPages.isVisible = event['url'] !== '/' + RouteNamesEnum.MapViewer;
    }

    // index 1 is the second element in header model
    menu[1].isVisible = event['url'] == '/' + RouteNamesEnum.MapViewer;

  }

  toggleSideMenu($event: boolean): void {
    this.menuService.toggleSideMenu($event);
  }

  private notifyShow(props: any) {
    if (props.type?.includes('notification'))
      this.notifyService.showContainer({
        component: NotifyEventComponent,
        props: props,
      });
    else {
      this.notifyService.showContainer({
        component: NotifyEventCameraComponent,
        props: props,
        // clickDispatcher: this.clickDispatcher
      });
    }
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    if (this.messageSubscription) {
      this.messageSubscription.unsubscribe();
    }
    if (this.clickSubscription) {
      this.clickSubscription.unsubscribe();
    }
  }

  private handleMenuLoaded(model: IHeaderModel<any>): void {
    if (model.menus) {
      this.menuService.headerMenu$.next(model);
    }

    this.headerModel$.next(model);
  }

  private handleRouteEvents(event: Event): void {
    if (event instanceof NavigationEnd) {
      const url = `${event?.url}`.replace('/', '');
      this.currentRoute$.next(url as RouteNamesEnum);
    }
  }

}
